'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'

import { editTradingAccount, TradingAccountUpdateRequest, updateTradingAccount } from '@api/orval/apiHooks'
import { DynamicActions } from '@components/DynamicActions/DynamicActions'
import { Action } from '@components/DynamicActions/types'
import { TextInputField } from '@components/Form/FormFields'
import { toApiPayload } from '@components/Form/formHelpers'
import { Button, DialogContent, DialogHeader, DialogTitle } from '@fp/ui-kit'
import { emitToast } from '@lib/emitToast'
import { EditAccountFormValues, tradingAccountFormSchema } from '@lib/formSchemas'

type FormFields = { name: EditAccountFormField; label: string }

type EditAccountFormField = keyof EditAccountFormValues

type EditAccountFormProps = {
  accountId: number
  onCancel: () => void
}

const fields: FormFields[] = [
  { name: 'min_trading_days', label: 'Min Trading Days' },
  { name: 'max_loss_percentage', label: 'Max Loss %' },
  { name: 'profit_target_percentage', label: 'Profit Target %' },
  { name: 'max_daily_loss_percentage', label: 'Max Daily Loss %' },
  { name: 'max_challenge_duration_days', label: 'Max Challenge Duration (Days)' },
  { name: 'profit_split_percentage', label: 'Profit Split %' },
  { name: 'daily_lotsize_limit', label: 'Daily Lotsize Limit' },
  { name: 'max_daily_floating_risk', label: 'Max Daily Floating Risk' },
]

export function EditAccountForm({ accountId, onCancel }: EditAccountFormProps) {
  const {
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<EditAccountFormValues>({ resolver: zodResolver(tradingAccountFormSchema), mode: 'onBlur' })
  const [actions, setActions] = useState<Action[]>([])

  useEffect(() => {
    const fetchEditData = async () => {
      try {
        if (!accountId || typeof accountId !== 'number') {
          return
        }

        const data = await editTradingAccount(accountId)
        if (data) {
          reset({ ...data })
          if (data.actions) {
            setActions(data.actions)
          }
        }
      } catch (error) {
        console.error('Failed to fetch edit data:', error)
      }
    }

    fetchEditData()
  }, [accountId, reset])

  const onSubmit = async (data: EditAccountFormValues) => {
    try {
      const updateData: TradingAccountUpdateRequest = {
        trading_account: toApiPayload(data),
      }

      await updateTradingAccount(accountId, updateData)
      onCancel()
      emitToast({
        message: 'Trading account updated successfully',
        type: 'success',
      })
    } catch {
      emitToast({
        message: 'Failed to update trading account, please try again.',
      })
    }
  }

  return (
    <>
      <DialogContent className='sm:min-w-lg'>
        <DialogHeader>
          <div className='flex items-center justify-between gap-3'>
            <DialogTitle>Edit Account</DialogTitle>
            <DynamicActions
              type='trading_account'
              itemId={accountId}
              actions={actions}
              actionFilter='hide_all'
              className='mr-4'
            />
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className='space-y-6 max-w-xl'>
          <div className='grid sm:grid-cols-2 gap-4 justify-center'>
            {fields.map(({ name, label }) => (
              <TextInputField
                key={name}
                name={name}
                label={label}
                control={control}
                type='number'
                min={0}
                error={errors[name]?.message}
              />
            ))}
          </div>

          <Button type='submit' size='block' variant='primary'>
            Save
          </Button>
        </form>
      </DialogContent>
    </>
  )
}
